import type { CmsContentAboutServicesForm } from "@/modules/cms/domain/contracts";
import { applyStockServiceAutofill } from "@/modules/cms/presentation/ui/forms/apply-stock-service-autofill";
import {
  stockPublicApi,
  type StockPublicServiceDTO,
} from "@/modules/stock-public/presentation/api/client";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import {
  Badge,
  cn,
  FormCheckbox,
  Label,
  RHF,
} from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import type { Control, UseFormSetValue } from "react-hook-form";

function formatPrice(price: number): string {
  return `${price.toLocaleString("en-US")} ₭`;
}

function resolveBaseUnitPrice(svc: StockPublicServiceDTO): number {
  const sorted = [...(svc.priceTiers ?? [])].sort(
    (a, b) => a.minQuantity - b.minQuantity,
  );
  for (const tier of sorted) {
    const unitPrice = Number(tier.unitPrice);
    if (Number.isFinite(unitPrice) && unitPrice >= 0) {
      return unitPrice;
    }
  }
  return 0;
}

function getServiceLabel(svc: StockPublicServiceDTO): string {
  const name = svc.nameEn?.trim() ? `${svc.name} (${svc.nameEn})` : svc.name;
  return `${svc.code} — ${name}`;
}

function stockServicesQueryFn(params: {
  search: string;
  pageParam: number;
}): Promise<{ items: StockPublicServiceDTO[]; nextPage: number | null }> {
  return stockPublicApi
    .list({ q: params.search || undefined, activeOnly: false, limit: 100 })
    .then((items) => ({
      items,
      nextPage: null,
    }));
}

async function preloadStockService(
  code: string,
): Promise<StockPublicServiceDTO | null> {
  if (!code) return null;
  try {
    return await stockPublicApi.get(code);
  } catch {
    return null;
  }
}

type StockServicePickerProps = {
  control: Control<CmsContentAboutServicesForm>;
  setValue: UseFormSetValue<CmsContentAboutServicesForm>;
  serviceIndex: number;
  /** รายการใหม่ที่ยังไม่เลือก Stock */
  pendingSelection?: boolean;
};

export function StockServicePicker({
  control,
  setValue,
  serviceIndex,
  pendingSelection = false,
}: StockServicePickerProps) {
  const codePath = `items.${serviceIndex}.stockServiceCode` as const;
  const showPricePath = `items.${serviceIndex}.showStockPrice` as const;
  const selectedCode = (RHF.useWatch({ control, name: codePath }) ?? "") as string;
  const showStockPrice = RHF.useWatch({ control, name: showPricePath });

  const { data: preview, isLoading: previewLoading, isError } = useQuery({
    queryKey: ["stock-public", "preview", selectedCode],
    queryFn: () => preloadStockService(selectedCode),
    enabled: !!selectedCode?.trim(),
    staleTime: 30_000,
  });

  const handleSelect = async (code: string) => {
    const imagePath = `items.${serviceIndex}.imageKey` as const;
    if (!code) {
      setValue(codePath, null, { shouldDirty: true });
      setValue(showPricePath, false, { shouldDirty: true });
      setValue(imagePath, "", { shouldDirty: true });
      return;
    }

    const dto = await preloadStockService(code);
    if (dto) {
      applyStockServiceAutofill(setValue, serviceIndex, dto);
    } else {
      setValue(codePath, code, { shouldDirty: true });
      setValue(showPricePath, true, { shouldDirty: true });
    }
  };

  return (
    <div className="space-y-3">
      {pendingSelection && !selectedCode && (
        <p className="font-medium text-primary text-sm">
          ເລືອກບໍລິການ Stock ກ່ອນ — ລະບົບຈະເຕີມຫົວຂໍ້, slug ແລະເນື້ອຫາໃຫ້ອັດຕະໂນມັດ
        </p>
      )}

      <div className="space-y-2">
        <Label>ບໍລິການ Stock POS</Label>
        <RHF.Controller
          control={control}
          name={codePath}
          render={({ field }) => (
            <InfiniteCombobox<StockPublicServiceDTO>
              queryKey={["stock-public", "services"]}
              queryFn={stockServicesQueryFn}
              preloadQueryFn={preloadStockService}
              getLabel={getServiceLabel}
              getValue={(item) => item.code}
              value={field.value ?? ""}
              onValueChange={(code) => void handleSelect(code)}
              placeholder="ຄົ້ນຫາ / ເລືອກບໍລິການ Stock..."
              clearable
            />
          )}
        />
      </div>

      {selectedCode && (
        <>
          <FormCheckbox
            name={showPricePath}
            label="ສະແດງລາຄາຈາກ Stock ບົນເວັບ"
          />

          <div
            className={cn(
              "rounded-md border bg-background p-3 text-sm",
              isError && "border-destructive/50",
            )}
          >
            <span className="font-medium text-xs uppercase tracking-wide text-muted-foreground">
              Preview Stock
            </span>
            {previewLoading && (
              <p className="mt-2 text-muted-foreground text-xs">ກຳລັງໂຫຼດ...</p>
            )}
            {isError && !previewLoading && (
              <p className="mt-2 text-destructive text-xs">
                ເຊື່ອມ Stock API ບໍ່ໄດ້ — ກວດ STOCK_PUBLIC_API_URL / KEY
              </p>
            )}
            {preview && (
              <div className="mt-2 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{preview.name}</span>
                  {!preview.isActive && (
                    <Badge variant="outline" className="border-amber-500 text-amber-700">
                      ປິດໃຊ້ງານ — ບໍ່ສະແດງລາຄາບົນເວັບ
                    </Badge>
                  )}
                </div>
                <p className="text-muted-foreground text-xs">
                  {formatPrice(resolveBaseUnitPrice(preview))} / {preview.unit}
                  {preview.categoryName ? ` · ${preview.categoryName}` : ""}
                </p>
                {showStockPrice !== false && !preview.isActive && (
                  <p className="text-amber-700 text-xs">
                    ເປີດສະແດງລາຄາແຕ່ Stock ປິດໃຊ້ງານ — ເວັບຈະບໍ່ສະແດງລາຄາ
                  </p>
                )}
              </div>
            )}
            {!previewLoading && !preview && !isError && selectedCode && (
              <p className="mt-2 text-muted-foreground text-xs">
                ບໍ່ພົບບໍລິການ {selectedCode} ໃນ Stock
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
